/* ------------------------------------------------------------------ *
 *  Bu dosya Backend/Layer.Core (DTO + enum) kaynaklarindan uretilmistir.
 *  Elle duzenlemeyin; sozlesme degistiginde yeniden uretin.
 * ------------------------------------------------------------------ */

/** Tum uclarin dondugu standart zarf. */
export interface ApiResponse<T> {
  data: T | null;
  statusCode: number;
  error: ErrorsDto | null;
  messages: SuccessDto | null;
  traceId?: string | null;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

/** Veri tasimayan yanitlarin govdesi. */
export interface NoDataDto {
  readonly _empty?: never;
}

/** GET /api/admin/files/limits yaniti (anonim nesne). */
export interface FileLimitsDto {
  maxImageSizeMb: number;
  maxDocumentSizeMb: number;
  allowedImageExtensions: string[];
  allowedDocumentExtensions: string[];
}

/* ----------------------------- Enum'lar ----------------------------- */

export type AddressKind = 'Billing' | 'Shipping';
export const addressKindValues = ['Billing', 'Shipping'] as const;

export type AuditAction = 'Create' | 'Update' | 'Delete' | 'Login' | 'LoginFailed' | 'Logout' | 'PasswordChanged' | 'PasswordResetRequested' | 'PasswordReset' | 'EmailChanged' | 'EmailConfirmed' | 'TwoFactorEnabled' | 'TwoFactorDisabled' | 'RoleAssigned' | 'RoleRemoved' | 'AccountLocked' | 'AccountUnlocked' | 'Export' | 'Import' | 'BulkOperation' | 'Refund' | 'Cancel' | 'Fulfill' | 'SettingChanged' | 'TokenRevoked' | 'TokenReuseDetected';
export const auditActionValues = ['Create', 'Update', 'Delete', 'Login', 'LoginFailed', 'Logout', 'PasswordChanged', 'PasswordResetRequested', 'PasswordReset', 'EmailChanged', 'EmailConfirmed', 'TwoFactorEnabled', 'TwoFactorDisabled', 'RoleAssigned', 'RoleRemoved', 'AccountLocked', 'AccountUnlocked', 'Export', 'Import', 'BulkOperation', 'Refund', 'Cancel', 'Fulfill', 'SettingChanged', 'TokenRevoked', 'TokenReuseDetected'] as const;

export type BulkOperationStatus = 'Queued' | 'Running' | 'Completed' | 'CompletedWithErrors' | 'Failed' | 'Cancelled';
export const bulkOperationStatusValues = ['Queued', 'Running', 'Completed', 'CompletedWithErrors', 'Failed', 'Cancelled'] as const;

export type BulkOperationType = 'ProductImport' | 'ProductExport' | 'ProductBulkUpdate' | 'ProductBulkDelete' | 'InventoryImport' | 'InventoryBulkAdjust' | 'PriceBulkUpdate' | 'OrderExport' | 'OrderBulkFulfill' | 'CustomerImport' | 'CustomerExport' | 'ImageImport';
export const bulkOperationTypeValues = ['ProductImport', 'ProductExport', 'ProductBulkUpdate', 'ProductBulkDelete', 'InventoryImport', 'InventoryBulkAdjust', 'PriceBulkUpdate', 'OrderExport', 'OrderBulkFulfill', 'CustomerImport', 'CustomerExport', 'ImageImport'] as const;

export type CartStatus = 'Active' | 'Converted' | 'Abandoned' | 'Expired' | 'Merged';
export const cartStatusValues = ['Active', 'Converted', 'Abandoned', 'Expired', 'Merged'] as const;

export type CollectionKind = 'Manual' | 'Automated';
export const collectionKindValues = ['Manual', 'Automated'] as const;

export type CollectionRuleField = 'Title' | 'ProductKind' | 'Brand' | 'Tag' | 'Price' | 'CompareAtPrice' | 'Weight' | 'InventoryQuantity' | 'VariantTitle' | 'Sku' | 'Category' | 'CreatedAt';
export const collectionRuleFieldValues = ['Title', 'ProductKind', 'Brand', 'Tag', 'Price', 'CompareAtPrice', 'Weight', 'InventoryQuantity', 'VariantTitle', 'Sku', 'Category', 'CreatedAt'] as const;

export type CollectionRuleOperator = 'Equals' | 'NotEquals' | 'GreaterThan' | 'LessThan' | 'GreaterThanOrEqual' | 'LessThanOrEqual' | 'StartsWith' | 'EndsWith' | 'Contains' | 'NotContains' | 'IsSet' | 'IsNotSet';
export const collectionRuleOperatorValues = ['Equals', 'NotEquals', 'GreaterThan', 'LessThan', 'GreaterThanOrEqual', 'LessThanOrEqual', 'StartsWith', 'EndsWith', 'Contains', 'NotContains', 'IsSet', 'IsNotSet'] as const;

export type ContactMessageStatus = 'New' | 'Read' | 'Replied' | 'Closed' | 'Spam';
export const contactMessageStatusValues = ['New', 'Read', 'Replied', 'Closed', 'Spam'] as const;

export type DimensionUnit = 'Millimeter' | 'Centimeter' | 'Meter' | 'Inch';
export const dimensionUnitValues = ['Millimeter', 'Centimeter', 'Meter', 'Inch'] as const;

export type DiscountAllocationMethod = 'Across' | 'Each';
export const discountAllocationMethodValues = ['Across', 'Each'] as const;

export type DiscountCombination = 'None' | 'WithProductDiscounts' | 'WithShippingDiscounts' | 'WithOrderDiscounts' | 'All';
export const discountCombinationValues = ['None', 'WithProductDiscounts', 'WithShippingDiscounts', 'WithOrderDiscounts', 'All'] as const;

export type DiscountCustomerSelection = 'All' | 'SpecificCustomers' | 'CustomerGroups' | 'FirstTimeBuyers';
export const discountCustomerSelectionValues = ['All', 'SpecificCustomers', 'CustomerGroups', 'FirstTimeBuyers'] as const;

export type DiscountSelection = 'All' | 'EntitledProducts' | 'EntitledCategories' | 'EntitledCollections' | 'EntitledVariants';
export const discountSelectionValues = ['All', 'EntitledProducts', 'EntitledCategories', 'EntitledCollections', 'EntitledVariants'] as const;

export type DiscountStatus = 'Draft' | 'Scheduled' | 'Active' | 'Expired' | 'Disabled';
export const discountStatusValues = ['Draft', 'Scheduled', 'Active', 'Expired', 'Disabled'] as const;

export type DiscountTarget = 'LineItem' | 'Shipping' | 'Order';
export const discountTargetValues = ['LineItem', 'Shipping', 'Order'] as const;

export type DiscountType = 'Percentage' | 'FixedAmount' | 'FreeShipping' | 'BuyXGetY';
export const discountTypeValues = ['Percentage', 'FixedAmount', 'FreeShipping', 'BuyXGetY'] as const;

export type EmailOtpPurpose = 'TwoFactorLogin' | 'EmailVerification' | 'PasswordReset' | 'EmailChange' | 'SensitiveAction';
export const emailOtpPurposeValues = ['TwoFactorLogin', 'EmailVerification', 'PasswordReset', 'EmailChange', 'SensitiveAction'] as const;

export type FinancialStatus = 'Pending' | 'Authorized' | 'PartiallyPaid' | 'Paid' | 'PartiallyRefunded' | 'Refunded' | 'Voided' | 'Expired';
export const financialStatusValues = ['Pending', 'Authorized', 'PartiallyPaid', 'Paid', 'PartiallyRefunded', 'Refunded', 'Voided', 'Expired'] as const;

export type FulfillmentState = 'Pending' | 'LabelCreated' | 'PickedUp' | 'InTransit' | 'OutForDelivery' | 'Delivered' | 'AttemptedDelivery' | 'Exception' | 'Cancelled' | 'Returned';
export const fulfillmentStateValues = ['Pending', 'LabelCreated', 'PickedUp', 'InTransit', 'OutForDelivery', 'Delivered', 'AttemptedDelivery', 'Exception', 'Cancelled', 'Returned'] as const;

export type FulfillmentStatus = 'Unfulfilled' | 'PartiallyFulfilled' | 'Fulfilled' | 'Restocked' | 'Cancelled' | 'OnHold';
export const fulfillmentStatusValues = ['Unfulfilled', 'PartiallyFulfilled', 'Fulfilled', 'Restocked', 'Cancelled', 'OnHold'] as const;

export type GiftCardStatus = 'Active' | 'PartiallyRedeemed' | 'Redeemed' | 'Expired' | 'Disabled';
export const giftCardStatusValues = ['Active', 'PartiallyRedeemed', 'Redeemed', 'Expired', 'Disabled'] as const;

export type GiftCardTransactionKind = 'Issue' | 'Redeem' | 'Refund' | 'Adjustment' | 'Expire';
export const giftCardTransactionKindValues = ['Issue', 'Redeem', 'Refund', 'Adjustment', 'Expire'] as const;

export type InventoryMovementType = 'Adjustment' | 'Sale' | 'ReturnRestock' | 'TransferOut' | 'TransferIn' | 'Receipt' | 'Damage' | 'Loss' | 'Reservation' | 'ReservationRelease' | 'Fulfillment' | 'Cancellation' | 'BulkImport';
export const inventoryMovementTypeValues = ['Adjustment', 'Sale', 'ReturnRestock', 'TransferOut', 'TransferIn', 'Receipt', 'Damage', 'Loss', 'Reservation', 'ReservationRelease', 'Fulfillment', 'Cancellation', 'BulkImport'] as const;

export type InventoryPolicy = 'Deny' | 'Continue';
export const inventoryPolicyValues = ['Deny', 'Continue'] as const;

export type LocationKind = 'Warehouse' | 'RetailStore' | 'Dropshipper' | 'Virtual';
export const locationKindValues = ['Warehouse', 'RetailStore', 'Dropshipper', 'Virtual'] as const;

export type LogLevelType = 'Trace' | 'Debug' | 'Information' | 'Warning' | 'Error' | 'Critical';
export const logLevelTypeValues = ['Trace', 'Debug', 'Information', 'Warning', 'Error', 'Critical'] as const;

export type LoginAttemptResult = 'Success' | 'InvalidPassword' | 'UserNotFound' | 'EmailNotConfirmed' | 'LockedOut' | 'Disabled' | 'TwoFactorRequired' | 'TwoFactorFailed' | 'RateLimited';
export const loginAttemptResultValues = ['Success', 'InvalidPassword', 'UserNotFound', 'EmailNotConfirmed', 'LockedOut', 'Disabled', 'TwoFactorRequired', 'TwoFactorFailed', 'RateLimited'] as const;

export type MediaKind = 'Image' | 'Video' | 'ExternalVideo' | 'Model3D' | 'Document';
export const mediaKindValues = ['Image', 'Video', 'ExternalVideo', 'Model3D', 'Document'] as const;

export type MetafieldOwnerType = 'Product' | 'ProductVariant' | 'Category' | 'Collection' | 'Customer' | 'Order' | 'Brand' | 'Page' | 'Store';
export const metafieldOwnerTypeValues = ['Product', 'ProductVariant', 'Category', 'Collection', 'Customer', 'Order', 'Brand', 'Page', 'Store'] as const;

export type MetafieldValueType = 'String' | 'Integer' | 'Decimal' | 'Boolean' | 'Json' | 'Date' | 'DateTime' | 'Url' | 'Color' | 'RichText';
export const metafieldValueTypeValues = ['String', 'Integer', 'Decimal', 'Boolean', 'Json', 'Date', 'DateTime', 'Url', 'Color', 'RichText'] as const;

export type OrderCancelReason = 'CustomerRequest' | 'Fraud' | 'InventoryUnavailable' | 'PaymentDeclined' | 'Duplicate' | 'Other';
export const orderCancelReasonValues = ['CustomerRequest', 'Fraud', 'InventoryUnavailable', 'PaymentDeclined', 'Duplicate', 'Other'] as const;

export type OrderChannel = 'Web' | 'Mobile' | 'Pos' | 'Marketplace' | 'AdminPanel' | 'Api' | 'Phone';
export const orderChannelValues = ['Web', 'Mobile', 'Pos', 'Marketplace', 'AdminPanel', 'Api', 'Phone'] as const;

export type OrderStatus = 'Draft' | 'Open' | 'Archived' | 'Cancelled';
export const orderStatusValues = ['Draft', 'Open', 'Archived', 'Cancelled'] as const;

export type OtpVerificationResult = 'Success' | 'NotFound' | 'Expired' | 'Invalid' | 'TooManyAttempts' | 'AlreadyUsed';
export const otpVerificationResultValues = ['Success', 'NotFound', 'Expired', 'Invalid', 'TooManyAttempts', 'AlreadyUsed'] as const;

export type OutboxStatus = 'Pending' | 'Processing' | 'Published' | 'Failed' | 'DeadLettered';
export const outboxStatusValues = ['Pending', 'Processing', 'Published', 'Failed', 'DeadLettered'] as const;

export type PaymentMethodType = 'CreditCard' | 'DebitCard' | 'BankTransfer' | 'CashOnDelivery' | 'GiftCard' | 'StoreCredit' | 'DigitalWallet' | 'BuyNowPayLater' | 'Installment';
export const paymentMethodTypeValues = ['CreditCard', 'DebitCard', 'BankTransfer', 'CashOnDelivery', 'GiftCard', 'StoreCredit', 'DigitalWallet', 'BuyNowPayLater', 'Installment'] as const;

export type PaymentTransactionKind = 'Authorization' | 'Capture' | 'Sale' | 'Void' | 'Refund';
export const paymentTransactionKindValues = ['Authorization', 'Capture', 'Sale', 'Void', 'Refund'] as const;

export type PaymentTransactionStatus = 'Pending' | 'Processing' | 'Success' | 'Failure' | 'Error' | 'Cancelled';
export const paymentTransactionStatusValues = ['Pending', 'Processing', 'Success', 'Failure', 'Error', 'Cancelled'] as const;

export type PriceListAdjustmentType = 'FixedPrice' | 'PercentageOff' | 'AmountOff';
export const priceListAdjustmentTypeValues = ['FixedPrice', 'PercentageOff', 'AmountOff'] as const;

export type ProductKind = 'Physical' | 'Digital' | 'Service' | 'GiftCard' | 'Bundle';
export const productKindValues = ['Physical', 'Digital', 'Service', 'GiftCard', 'Bundle'] as const;

export type ProductStatus = 'Draft' | 'Active' | 'Archived';
export const productStatusValues = ['Draft', 'Active', 'Archived'] as const;

export type PurchaseOrderStatus = 'Draft' | 'Ordered' | 'PartiallyReceived' | 'Received' | 'Cancelled' | 'Closed';
export const purchaseOrderStatusValues = ['Draft', 'Ordered', 'PartiallyReceived', 'Received', 'Cancelled', 'Closed'] as const;

export type RefundStatus = 'Pending' | 'Processing' | 'Completed' | 'Failed' | 'Cancelled';
export const refundStatusValues = ['Pending', 'Processing', 'Completed', 'Failed', 'Cancelled'] as const;

export type ReturnDisposition = 'Restock' | 'Damaged' | 'Discard' | 'ReturnToSupplier';
export const returnDispositionValues = ['Restock', 'Damaged', 'Discard', 'ReturnToSupplier'] as const;

export type ReturnReason = 'Damaged' | 'Defective' | 'NotAsDescribed' | 'WrongItemSent' | 'SizeTooSmall' | 'SizeTooLarge' | 'ChangedMind' | 'ArrivedLate' | 'MissingParts' | 'Other';
export const returnReasonValues = ['Damaged', 'Defective', 'NotAsDescribed', 'WrongItemSent', 'SizeTooSmall', 'SizeTooLarge', 'ChangedMind', 'ArrivedLate', 'MissingParts', 'Other'] as const;

export type ReturnStatus = 'Requested' | 'Approved' | 'Rejected' | 'InTransit' | 'Received' | 'Inspecting' | 'Completed' | 'Cancelled';
export const returnStatusValues = ['Requested', 'Approved', 'Rejected', 'InTransit', 'Received', 'Inspecting', 'Completed', 'Cancelled'] as const;

export type ReviewStatus = 'Pending' | 'Approved' | 'Rejected' | 'Spam';
export const reviewStatusValues = ['Pending', 'Approved', 'Rejected', 'Spam'] as const;

export type ShipmentTrackingStatus = 'Unknown' | 'LabelCreated' | 'PickedUp' | 'InTransit' | 'AtDeliveryBranch' | 'OutForDelivery' | 'Delivered' | 'AttemptedDelivery' | 'Exception' | 'ReturnedToSender' | 'Cancelled';
export const shipmentTrackingStatusValues = ['Unknown', 'LabelCreated', 'PickedUp', 'InTransit', 'AtDeliveryBranch', 'OutForDelivery', 'Delivered', 'AttemptedDelivery', 'Exception', 'ReturnedToSender', 'Cancelled'] as const;

export type ShippingRateType = 'Flat' | 'PriceBased' | 'WeightBased' | 'ItemBased' | 'CarrierCalculated' | 'Free';
export const shippingRateTypeValues = ['Flat', 'PriceBased', 'WeightBased', 'ItemBased', 'CarrierCalculated', 'Free'] as const;

export type ShippingSpeed = 'Standard' | 'Express' | 'SameDay' | 'NextDay' | 'Economy' | 'StorePickup';
export const shippingSpeedValues = ['Standard', 'Express', 'SameDay', 'NextDay', 'Economy', 'StorePickup'] as const;

export type ShippingZoneScope = 'Country' | 'Province' | 'PostalCode' | 'RestOfWorld';
export const shippingZoneScopeValues = ['Country', 'Province', 'PostalCode', 'RestOfWorld'] as const;

export type StockReservationStatus = 'Active' | 'Released' | 'Committed' | 'Expired';
export const stockReservationStatusValues = ['Active', 'Released', 'Committed', 'Expired'] as const;

export type StockTransferStatus = 'Draft' | 'Requested' | 'InTransit' | 'PartiallyReceived' | 'Received' | 'Cancelled';
export const stockTransferStatusValues = ['Draft', 'Requested', 'InTransit', 'PartiallyReceived', 'Received', 'Cancelled'] as const;

export type TaxInclusionMode = 'Exclusive' | 'Inclusive';
export const taxInclusionModeValues = ['Exclusive', 'Inclusive'] as const;

export type WebhookDeliveryStatus = 'Pending' | 'Delivered' | 'Failed' | 'Retrying' | 'DeadLettered';
export const webhookDeliveryStatusValues = ['Pending', 'Delivered', 'Failed', 'Retrying', 'DeadLettered'] as const;

export type WeightUnit = 'Gram' | 'Kilogram' | 'Pound' | 'Ounce';
export const weightUnitValues = ['Gram', 'Kilogram', 'Pound', 'Ounce'] as const;

/* ------------------------- Veri sozlesmeleri ------------------------ */

export interface SuccessDto {
  successData: string[];
  isShow: boolean;
}

export interface ErrorsDto {
  errors: string[];
  isShow: boolean;
  /** İstemcinin dile bağımlı olmadan hatayı ayırt edebilmesi için makine okunabilir kod. */
  code?: string | null;
}

export interface PagedRequest {
  /** 1'den baslar. */
  page?: number | null;
  /** Ust sinir 100'dur; asilirsa sunucu kirpar. */
  pageSize?: number | null;
  /** Serbest metin arama terimi. */
  search?: string | null;
  /** Sıralanacak alan adı. Beyaz liste dışındaki değerler servis katmanında yok sayılır. */
  sortBy?: string | null;
  sortDescending?: boolean | null;
}

export interface RegisterDto {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string | null;
  /** Pazarlama e-postalarına açık rıza; varsayılan kapalıdır. */
  acceptsMarketing: boolean;
  acceptsTerms: boolean;
}

export interface LoginDto {
  email: string;
  password: string;
  /** İşaretliyse refresh token daha uzun ömürlü verilir. */
  rememberMe: boolean;
}

export interface TokenDto {
  accessToken: string;
  accessTokenExpiration: string;
  tokenType: string;
  /** Access token'ın saniye cinsinden kalan ömrü; istemci yenilemeyi buna göre zamanlar. */
  expiresInSeconds: number;
}

export interface LoginResultDto {
  requiresTwoFactor: boolean;
  /** İkinci adımı tamamlamak için kullanılan, kısa ömürlü ve tek amaçlı ara token. Kullanıcı kimliği istemciye asla açık gönderilmez — böylece saldırgan başka bir kullanıcının kimliğiyle doğrulama adımını tetikleyemez. */
  twoFactorToken?: string | null;
  /** Kodun gönderildiği maskelenmiş adres: "a***n@example.com". */
  maskedEmail?: string | null;
  token?: TokenDto | null;
  user?: CurrentUserDto | null;
  /** Ham refresh token. JsonIgnore ile işaretlidir: yanıt gövdesine asla yazılmaz, yalnızca controller'ın HttpOnly çereze yazması için taşınır. Böylece token JavaScript tarafından okunamaz ve XSS ile çalınamaz. */
  refreshToken?: string | null;
  refreshTokenExpiresAt?: string | null;
}

export interface VerifyTwoFactorDto {
  twoFactorToken: string;
  code: string;
  rememberMe: boolean;
}

export interface ResendTwoFactorCodeDto {
  twoFactorToken: string;
}

export interface ForgotPasswordDto {
  email: string;
}

export interface ResetPasswordDto {
  email: string;
  token: string;
  newPassword: string;
  confirmNewPassword: string;
}

export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
  /** Diğer cihazlardaki oturumlar da kapatılsın mı. Varsayılan açık; parola değişikliğinin en yaygın sebebi hesabın ele geçirilmiş olmasıdır. */
  signOutOtherSessions: boolean;
}

export interface ConfirmEmailDto {
  email: string;
  token: string;
}

export interface ResendConfirmationDto {
  email: string;
}

export interface ChangeEmailRequestDto {
  newEmail: string;
  /** Kimlik doğrulama için mevcut parola istenir. */
  currentPassword: string;
}

export interface ConfirmEmailChangeDto {
  newEmail: string;
  token: string;
}

export interface EnableTwoFactorDto {
  /** 2FA açmak da hassas bir işlem olduğu için parola doğrulaması istenir. */
  currentPassword: string;
}

export interface CurrentUserDto {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  phoneNumber?: string | null;
  profilePictureUrl?: string | null;
  emailConfirmed: boolean;
  twoFactorEnabled: boolean;
  acceptsMarketing: boolean;
  dateOfBirth?: string | null;
  customerGroupName?: string | null;
  roles: string[];
  /** Rollerden türetilen ince taneli yetkiler; arayüz menüleri buna göre çizilir. */
  permissions: string[];
  lastLoginAt?: string | null;
  createdAt: string;
}

export interface UpdateProfileDto {
  firstName: string;
  lastName: string;
  phoneNumber?: string | null;
  dateOfBirth?: string | null;
  profilePictureUrl?: string | null;
  acceptsMarketing: boolean;
}

export interface UserSessionDto {
  id: number;
  deviceName?: string | null;
  userAgent?: string | null;
  ipAddress?: string | null;
  location?: string | null;
  createdAt: string;
  lastUsedAt?: string | null;
  expiresAt: string;
  /** İsteği yapan oturumun kendisi mi; arayüzde "bu cihaz" olarak işaretlenir. */
  isCurrent: boolean;
}

export interface CreateStaffUserDto {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string | null;
  roles: string[];
  /** Parola gönderilmez; kullanıcıya kurulum bağlantısı e-postayla iletilir. Böylece parola hiçbir zaman yönetici tarafından bilinmez. */
  sendInvitationEmail: boolean;
}

export interface UpdateUserRolesDto {
  roles: string[];
}

export interface SetUserActiveDto {
  isActive: boolean;
  reason?: string | null;
}

export interface ProductListItemDto {
  id: number;
  title: string;
  slug: string;
  shortDescription?: string | null;
  brandName?: string | null;
  brandSlug?: string | null;
  primaryImageUrl?: string | null;
  minPrice: number;
  maxPrice: number;
  compareAtPrice?: number | null;
  /** İndirim yüzdesi; rozet göstermek için hesaplanmış hâlde gelir. */
  discountPercentage?: number | null;
  currencyCode: string;
  isInStock: boolean;
  hasVariants: boolean;
  averageRating: number;
  reviewCount: number;
  isNewArrival: boolean;
  isFeatured: boolean;
  isPreOrder: boolean;
  priceWhenAdded?: number | null;
  notifyOnRestock?: boolean | null;
  notifyOnPriceDrop?: boolean | null;
}

export interface WishlistToggleResultDto {
  isFavorited: boolean;
  message: string;
}

export interface ProductDetailDto {
  id: number;
  title: string;
  slug: string;
  shortDescription?: string | null;
  description?: string | null;
  kind: ProductKind;
  brandId?: number | null;
  brandName?: string | null;
  brandSlug?: string | null;
  productType?: string | null;
  tags?: string | null;
  requiresShipping: boolean;
  isPreOrder: boolean;
  preOrderReleaseDate?: string | null;
  minimumOrderQuantity: number;
  maximumOrderQuantity: number;
  isAgeRestricted: boolean;
  minPrice: number;
  maxPrice: number;
  currencyCode: string;
  averageRating: number;
  reviewCount: number;
  breadcrumbs: CategoryBreadcrumbDto[];
  media: ProductMediaDto[];
  options: ProductOptionDto[];
  variants: ProductVariantDto[];
  /** Vitrinde gösterilmesine izin verilen özel alanlar. */
  specifications: MetafieldDto[];
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
  canonicalUrl?: string | null;
}

export interface ProductVariantDto {
  id: number;
  title: string;
  sku: string;
  barcode?: string | null;
  price: number;
  compareAtPrice?: number | null;
  currencyCode: string;
  /** Satılabilir stok. Kesin sayı yerine eşiğe göre kısıtlanabilir. */
  stockQuantity: number;
  isInStock: boolean;
  isLowStock: boolean;
  isPurchasable: boolean;
  imageUrl?: string | null;
  weight?: number | null;
  weightUnit: WeightUnit;
  /** Bu varyantı oluşturan eksen değerleri: {"Renk":"Kırmızı","Beden":"L"}. */
  optionValues: Record<string, string>;
  position: number;
  isActive: boolean;
}

export interface ProductOptionDto {
  id: number;
  name: string;
  displayType: string;
  position: number;
  values: ProductOptionValueDto[];
}

export interface ProductOptionValueDto {
  id: number;
  value: string;
  colorCode?: string | null;
  imageUrl?: string | null;
  position: number;
}

export interface ProductMediaDto {
  id: number;
  kind: MediaKind;
  url: string;
  thumbnailUrl?: string | null;
  altText?: string | null;
  position: number;
  isPrimary: boolean;
  width?: number | null;
  height?: number | null;
}

export interface CategoryBreadcrumbDto {
  id: number;
  name: string;
  slug: string;
}

export interface MetafieldDto {
  id: number;
  namespace: string;
  key: string;
  label?: string | null;
  value?: string | null;
  valueType: MetafieldValueType;
  position: number;
}

export interface AdminProductListItemDto {
  id: number;
  title: string;
  slug: string;
  status: ProductStatus;
  primaryImageUrl?: string | null;
  brandName?: string | null;
  primaryCategoryName?: string | null;
  minPrice: number;
  maxPrice: number;
  totalStock: number;
  variantCount: number;
  soldCount: number;
  publishedAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface ProductUpsertDto {
  title: string;
  /** Boş bırakılırsa başlıktan otomatik üretilir. */
  slug?: string | null;
  shortDescription?: string | null;
  description?: string | null;
  status: ProductStatus;
  kind: ProductKind;
  brandId?: number | null;
  primaryCategoryId?: number | null;
  categoryIds: number[];
  vendor?: string | null;
  productType?: string | null;
  tags?: string | null;
  publishedAt?: string | null;
  requiresShipping: boolean;
  isTaxable: boolean;
  taxClassId?: number | null;
  harmonizedSystemCode?: string | null;
  countryOfOrigin?: string | null;
  minimumOrderQuantity: number;
  maximumOrderQuantity: number;
  isAgeRestricted: boolean;
  minimumAge?: number | null;
  isPreOrder: boolean;
  preOrderReleaseDate?: string | null;
  isFeatured: boolean;
  isNewArrival: boolean;
  excludeFromDiscounts: boolean;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
  noIndex: boolean;
  /** Varyant eksenleri. Boş bırakılırsa tek bir varsayılan varyant oluşturulur ve fiyat/stok oradan yönetilir. */
  options: ProductOptionUpsertDto[];
  variants: ProductVariantUpsertDto[];
  media: ProductMediaUpsertDto[];
  metafields: MetafieldUpsertDto[];
}

export interface ProductOptionUpsertDto {
  id?: number | null;
  name: string;
  displayType: string;
  position: number;
  values: ProductOptionValueUpsertDto[];
}

export interface ProductOptionValueUpsertDto {
  id?: number | null;
  value: string;
  colorCode?: string | null;
  imageUrl?: string | null;
  position: number;
}

export interface ProductVariantUpsertDto {
  id?: number | null;
  /** Boş bırakılırsa eksen değerlerinden üretilir. */
  title?: string | null;
  /** Boş bırakılırsa otomatik üretilir. */
  sku?: string | null;
  barcode?: string | null;
  manufacturerPartNumber?: string | null;
  price: number;
  compareAtPrice?: number | null;
  costPrice?: number | null;
  trackInventory: boolean;
  inventoryPolicy: InventoryPolicy;
  /** Başlangıç stoğu. Yalnızca yeni varyantta uygulanır; mevcut varyantın stoğu bu alanla değil, stok hareketleriyle değiştirilir — aksi hâlde stok defteri ile gerçek miktar birbirinden kopar. */
  initialStock?: number | null;
  lowStockThreshold: number;
  requiresShipping: boolean;
  weight?: number | null;
  weightUnit: WeightUnit;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  dimensionUnit: DimensionUnit;
  isActive: boolean;
  position: number;
  /** Bu varyantı tanımlayan eksen değerleri: {"Renk":"Kırmızı","Beden":"L"}. Options listesindeki her eksen için tam olarak bir değer içermelidir. */
  optionValues: Record<string, string>;
  imageUrl?: string | null;
  digitalFileUrl?: string | null;
  digitalDownloadLimit: number;
  digitalDownloadExpiryDays: number;
}

export interface ProductMediaUpsertDto {
  id?: number | null;
  kind: MediaKind;
  url: string;
  thumbnailUrl?: string | null;
  altText?: string | null;
  position: number;
  isPrimary: boolean;
}

export interface MetafieldUpsertDto {
  id?: number | null;
  namespace: string;
  key: string;
  value?: string | null;
  valueType: MetafieldValueType;
  label?: string | null;
  isPublic: boolean;
  showOnStorefront: boolean;
  isFilterable: boolean;
  position: number;
}

export interface ProductQueryDto extends PagedRequest {
  categorySlug?: string | null;
  categoryId?: number | null;
  categoryIds?: number[] | null;
  /** Alt kategorilerdeki ürünler de dahil edilsin mi. */
  includeSubcategories?: boolean | null;
  brandSlug?: string | null;
  brandIds?: number[] | null;
  collectionSlug?: string | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  inStockOnly?: boolean | null;
  featuredOnly?: boolean | null;
  onSaleOnly?: boolean | null;
  minRating?: number | null;
  /** Etiket filtreleri (VE mantığı). */
  tags?: string[] | null;
  /** Özellik filtreleri: {"Renk":["Kırmızı","Mavi"]}. Aynı özellik içinde VEYA, farklı özellikler arasında VE mantığı uygulanır. */
  optionFilters?: Record<string, string[]> | null;
}

export interface AdminProductQueryDto extends PagedRequest {
  status?: ProductStatus | null;
  categoryId?: number | null;
  brandId?: number | null;
  kind?: ProductKind | null;
  /** Stoğu düşük olanlar. */
  lowStockOnly?: boolean | null;
  /** Stoğu tükenmiş olanlar. */
  outOfStockOnly?: boolean | null;
  /** Silinmiş kayıtları da getir. */
  includeDeleted?: boolean | null;
  createdFrom?: string | null;
  createdTo?: string | null;
}

export interface ProductFacetsDto {
  minPrice: number;
  maxPrice: number;
  brands: FacetItemDto[];
  categories: FacetItemDto[];
  options: Record<string, FacetItemDto[]>;
}

export interface FacetItemDto {
  value: string;
  label?: string | null;
  colorCode?: string | null;
  id?: number | null;
  /** Bu filtreyi seçince kaç ürün kalacağı. */
  count: number;
}

export interface CategoryDto {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  parentId?: number | null;
  level: number;
  imageUrl?: string | null;
  iconUrl?: string | null;
  bannerUrl?: string | null;
  isActive: boolean;
  showInMenu: boolean;
  isFeatured: boolean;
  displayOrder: number;
  productCount: number;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
  /** Ağaç görünümünde alt kategoriler. */
  children: CategoryDto[];
}

export interface CategoryUpsertDto {
  name: string;
  slug?: string | null;
  description?: string | null;
  parentId?: number | null;
  imageUrl?: string | null;
  iconUrl?: string | null;
  bannerUrl?: string | null;
  isActive: boolean;
  showInMenu: boolean;
  isFeatured: boolean;
  displayOrder: number;
  defaultTaxClassId?: number | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
}

export interface CategoryReorderDto {
  items: CategoryPositionDto[];
}

export interface CategoryPositionDto {
  id: number;
  parentId?: number | null;
  displayOrder: number;
}

export interface BrandDto {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  logoUrl?: string | null;
  bannerUrl?: string | null;
  websiteUrl?: string | null;
  countryCode?: string | null;
  isActive: boolean;
  isFeatured: boolean;
  displayOrder: number;
  productCount: number;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
}

export interface BrandUpsertDto {
  name: string;
  slug?: string | null;
  description?: string | null;
  logoUrl?: string | null;
  bannerUrl?: string | null;
  websiteUrl?: string | null;
  countryCode?: string | null;
  isActive: boolean;
  isFeatured: boolean;
  displayOrder: number;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
}

export interface CollectionDto {
  id: number;
  title: string;
  slug: string;
  description?: string | null;
  kind: CollectionKind;
  imageUrl?: string | null;
  bannerUrl?: string | null;
  isActive: boolean;
  isFeatured: boolean;
  displayOrder: number;
  sortOrder: string;
  productCount: number;
  publishedAt?: string | null;
  expiresAt?: string | null;
  lastRefreshedAt?: string | null;
  rules: CollectionRuleDto[];
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
}

export interface CollectionRuleDto {
  id: number;
  field: CollectionRuleField;
  operator: CollectionRuleOperator;
  value: string;
  position: number;
}

export interface CollectionUpsertDto {
  title: string;
  slug?: string | null;
  description?: string | null;
  kind: CollectionKind;
  matchAllRules: boolean;
  imageUrl?: string | null;
  bannerUrl?: string | null;
  isActive: boolean;
  isFeatured: boolean;
  displayOrder: number;
  publishedAt?: string | null;
  expiresAt?: string | null;
  sortOrder: string;
  /** Elle yönetilen koleksiyonlarda ürün kimlikleri. */
  productIds: number[];
  /** Otomatik koleksiyonlarda eşleşme kuralları. */
  rules: CollectionRuleUpsertDto[];
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
}

export interface CollectionRuleUpsertDto {
  field: CollectionRuleField;
  operator: CollectionRuleOperator;
  value: string;
  position: number;
}

export interface ProductReviewDto {
  id: number;
  productId: number;
  productTitle?: string | null;
  authorName: string;
  rating: number;
  title?: string | null;
  comment: string;
  isVerifiedPurchase: boolean;
  helpfulCount: number;
  notHelpfulCount: number;
  /** Kullanıcının bu değerlendirmeye verdiği güncel oy (true: faydalı, false: faydalı değil, null: oy yok) */
  currentUserVote?: boolean | null;
  imageUrls: string[];
  storeReply?: string | null;
  storeRepliedAt?: string | null;
  createdAt: string;
  /** Yalnızca yönetim görünümünde doldurulur. */
  status?: ReviewStatus | null;
  authorEmail?: string | null;
}

export interface ReviewVoteResultDto {
  reviewId: number;
  helpfulCount: number;
  notHelpfulCount: number;
  currentUserVote?: boolean | null;
}

export interface ProductReviewCreateDto {
  productId: number;
  productVariantId?: number | null;
  /** Doğrulanmış alıcı rozetini almak için sipariş numarası. */
  orderNumber?: string | null;
  rating: number;
  title?: string | null;
  comment: string;
  /** Misafir yorumlarında zorunlu. */
  authorName?: string | null;
  authorEmail?: string | null;
  imageUrls: string[];
}

export interface ReviewModerationDto {
  status: ReviewStatus;
  moderationNote?: string | null;
}

export interface ReviewReplyDto {
  reply: string;
}

export interface ReviewQueryDto extends PagedRequest {
  productId?: number | null;
  status?: ReviewStatus | null;
  rating?: number | null;
  verifiedOnly?: boolean | null;
}

export interface ReviewSummaryDto {
  averageRating: number;
  totalCount: number;
  /** Puan -> adet: {5: 120, 4: 30, ...} */
  ratingBreakdown: Record<number, number>;
  verifiedPurchaseCount: number;
}

export interface ReviewEligibilityDto {
  canReview: boolean;
  hasPurchased: boolean;
  alreadyReviewed: boolean;
  orderNumber?: string | null;
  reason?: string | null;
}

export interface PageDto {
  id: number;
  title: string;
  slug: string;
  content: string;
  isPublished: boolean;
  publishedAt?: string | null;
  isSystemPage: boolean;
  showInFooter: boolean;
  displayOrder: number;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
  noIndex?: boolean;
  updatedAt?: string | null;
}

export interface PageUpsertDto {
  title: string;
  slug?: string | null;
  content: string;
  isPublished: boolean;
  showInFooter: boolean;
  displayOrder: number;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
  noIndex: boolean;
}

export interface BlogPostDto {
  id: number;
  title: string;
  slug: string;
  summary?: string | null;
  content?: string | null;
  coverImageUrl?: string | null;
  authorName?: string | null;
  isPublished: boolean;
  publishedAt?: string | null;
  tags?: string | null;
  categoryName?: string | null;
  viewCount: number;
  readingTimeMinutes: number;
  featuredProductIds: number[];
  seoTitle?: string | null;
  seoDescription?: string | null;
}

export interface BlogPostUpsertDto {
  title: string;
  slug?: string | null;
  summary?: string | null;
  content: string;
  coverImageUrl?: string | null;
  authorName?: string | null;
  isPublished: boolean;
  publishedAt?: string | null;
  tags?: string | null;
  categoryName?: string | null;
  featuredProductIds: number[];
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
}

export interface BannerDto {
  id: number;
  title: string;
  subtitle?: string | null;
  imageUrl: string;
  mobileImageUrl?: string | null;
  linkUrl?: string | null;
  buttonText?: string | null;
  placement: string;
  isActive: boolean;
  displayOrder: number;
  startsAt?: string | null;
  endsAt?: string | null;
  altText?: string | null;
}

export interface BannerUpsertDto {
  title: string;
  subtitle?: string | null;
  imageUrl: string;
  mobileImageUrl?: string | null;
  linkUrl?: string | null;
  buttonText?: string | null;
  placement: string;
  isActive: boolean;
  displayOrder: number;
  startsAt?: string | null;
  endsAt?: string | null;
  altText?: string | null;
}

export interface MenuItemDto {
  id: number;
  menuKey: string;
  label: string;
  url?: string | null;
  linkType: string;
  targetId?: number | null;
  parentId?: number | null;
  iconUrl?: string | null;
  badgeText?: string | null;
  openInNewTab: boolean;
  isActive: boolean;
  displayOrder: number;
  children: MenuItemDto[];
}

export interface MenuItemUpsertDto {
  menuKey: string;
  label: string;
  url?: string | null;
  linkType: string;
  targetId?: number | null;
  parentId?: number | null;
  iconUrl?: string | null;
  badgeText?: string | null;
  openInNewTab: boolean;
  isActive: boolean;
  displayOrder: number;
}

export interface FaqItemDto {
  id: number;
  question: string;
  answer: string;
  category?: string | null;
  isActive: boolean;
  displayOrder: number;
  productId?: number | null;
}

export interface FaqItemUpsertDto {
  question: string;
  answer: string;
  category?: string | null;
  isActive: boolean;
  displayOrder: number;
  productId?: number | null;
}

export interface ContactMessageDto {
  id: number;
  fullName: string;
  email: string;
  phone?: string | null;
  subject?: string | null;
  message: string;
  status: ContactMessageStatus;
  relatedOrderNumber?: string | null;
  staffReply?: string | null;
  repliedAt?: string | null;
  internalNote?: string | null;
  createdAt: string;
}

export interface ContactMessageCreateDto {
  fullName: string;
  email: string;
  phone?: string | null;
  subject?: string | null;
  message: string;
  relatedOrderNumber?: string | null;
}

export interface ContactReplyDto {
  reply: string;
  sendEmail: boolean;
}

export interface ContactQueryDto extends PagedRequest {
  status?: ContactMessageStatus | null;
}

export interface NewsletterSubscribeDto {
  email: string;
  fullName?: string | null;
  source?: string | null;
}

export interface NewsletterSubscriberDto {
  id: number;
  email: string;
  fullName?: string | null;
  isConfirmed: boolean;
  isUnsubscribed: boolean;
  source?: string | null;
  createdAt: string;
}

export interface NewsletterConfirmResultDto {
  message: string;
  couponCode: string;
  discountAmount: number;
  minimumSubtotal?: number | null;
  isAssignedToAccount: boolean;
  alreadyConfirmed: boolean;
}

export interface StoreSettingDto {
  group: string;
  key: string;
  value?: string | null;
  valueType: string;
  description?: string | null;
  isPublic: boolean;
}

export interface UpdateSettingsDto {
  settings: Record<string, string>;
}

export interface LocationDto {
  id: number;
  name: string;
  code: string;
  kind: LocationKind;
  isActive: boolean;
  fulfillsOnlineOrders: boolean;
  supportsPickup: boolean;
  priority: number;
  addressLine1?: string | null;
  district?: string | null;
  city?: string | null;
  postalCode?: string | null;
  countryCode: string;
  phone?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface LocationUpsertDto {
  name: string;
  code: string;
  kind: LocationKind;
  isActive: boolean;
  fulfillsOnlineOrders: boolean;
  supportsPickup: boolean;
  countsTowardsStorefrontStock: boolean;
  priority: number;
  addressLine1?: string | null;
  addressLine2?: string | null;
  district?: string | null;
  city?: string | null;
  postalCode?: string | null;
  countryCode: string;
  phone?: string | null;
  email?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface InventoryLevelDto {
  inventoryItemId: number;
  locationId: number;
  locationName: string;
  onHand: number;
  committed: number;
  reserved: number;
  available: number;
  incoming: number;
  binLocation?: string | null;
  lastCountedAt?: string | null;
}

export interface InventoryItemDto {
  id: number;
  productVariantId: number;
  productId: number;
  productTitle: string;
  variantTitle: string;
  sku: string;
  barcode?: string | null;
  imageUrl?: string | null;
  isTracked: boolean;
  unitCost?: number | null;
  lowStockThreshold: number;
  totalAvailable: number;
  totalOnHand: number;
  isLowStock: boolean;
  reorderPoint?: number | null;
  supplierName?: string | null;
  levels: InventoryLevelDto[];
}

export interface InventoryAdjustmentDto {
  productVariantId: number;
  locationId: number;
  /** İşaretli değişim. Mutlak değer set etmek yerine fark verilmesi, eşzamanlı iki düzeltmenin birbirini ezmesini engeller. */
  quantityDelta: number;
  movementType: InventoryMovementType;
  reason?: string | null;
  note?: string | null;
}

export interface InventoryCountDto {
  productVariantId: number;
  locationId: number;
  countedQuantity: number;
  reason?: string | null;
}

export interface BulkInventoryAdjustmentDto {
  items: InventoryAdjustmentDto[];
  reason?: string | null;
}

export interface InventoryMovementDto {
  id: number;
  sku: string;
  productTitle: string;
  locationName: string;
  movementType: InventoryMovementType;
  quantityDelta: number;
  availableAfter: number;
  onHandAfter: number;
  referenceType?: string | null;
  referenceLabel?: string | null;
  reason?: string | null;
  note?: string | null;
  performedBy?: string | null;
  occurredAt: string;
}

export interface InventoryQueryDto extends PagedRequest {
  locationId?: number | null;
  productId?: number | null;
  lowStockOnly?: boolean | null;
  outOfStockOnly?: boolean | null;
  supplierId?: number | null;
  /** Yeniden sipariş noktasının altına düşenler. */
  belowReorderPoint?: boolean | null;
}

export interface InventoryMovementQueryDto extends PagedRequest {
  productVariantId?: number | null;
  locationId?: number | null;
  movementType?: InventoryMovementType | null;
  from?: string | null;
  to?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
}

export interface ReserveStockRequest {
  success: boolean;
  reservationId?: number | null;
  locationId?: number | null;
  /** Talep karşılanamadıysa gerçekte kaç adet ayrılabildiği. */
  availableQuantity: number;
  errorMessage?: string | null;
}

export interface FulfillmentAllocation {
  locationId: number;
  locationName: string;
  quantity: number;
}

export interface CustomerListItemDto {
  id: string;
  email: string;
  fullName?: string | null;
  phoneNumber?: string | null;
  emailConfirmed: boolean;
  isActive: boolean;
  twoFactorEnabled: boolean;
  customerGroupName?: string | null;
  totalOrderCount: number;
  totalSpent: number;
  lastOrderAt?: string | null;
  lastLoginAt?: string | null;
  createdAt: string;
  tags?: string | null;
  /** Hesap kilitliyse kilidin bittiği an. */
  lockoutEnd?: string | null;
}

export interface CustomerDetailDto extends CustomerListItemDto {
  dateOfBirth?: string | null;
  companyName?: string | null;
  taxOffice?: string | null;
  taxNumber?: string | null;
  acceptsMarketing: boolean;
  customerGroupId?: number | null;
  adminNote?: string | null;
  lastLoginIp?: string | null;
  accessFailedCount: number;
  addresses: CustomerAddressDto[];
  recentOrders: OrderListItemDto[];
  averageOrderValue: number;
}

export interface CustomerQueryDto extends PagedRequest {
  isActive?: boolean | null;
  emailConfirmed?: boolean | null;
  customerGroupId?: number | null;
  tag?: string | null;
  hasOrders?: boolean | null;
  minSpent?: number | null;
  registeredFrom?: string | null;
  registeredTo?: string | null;
}

export interface UpdateCustomerAdminDto {
  customerGroupId?: number | null;
  adminNote?: string | null;
  tags?: string | null;
}

export interface StaffUserDto {
  id: string;
  email: string;
  fullName?: string | null;
  phoneNumber?: string | null;
  isActive: boolean;
  emailConfirmed: boolean;
  twoFactorEnabled: boolean;
  roles: string[];
  lastLoginAt?: string | null;
  createdAt: string;
  activeSessionCount: number;
}

export interface RoleDto {
  id: string;
  name: string;
  description?: string | null;
  isSystemRole: boolean;
  userCount: number;
  permissions: string[];
}

export interface BulkOperationDto {
  publicId: string;
  operationType: BulkOperationType;
  status: BulkOperationStatus;
  totalCount: number;
  processedCount: number;
  successCount: number;
  errorCount: number;
  skippedCount: number;
  progressPercentage: number;
  inputFileName?: string | null;
  resultFileUrl?: string | null;
  errorReportUrl?: string | null;
  errorSummary: string[];
  startedAt?: string | null;
  completedAt?: string | null;
  failureReason?: string | null;
  createdAt: string;
}

export interface BulkPriceUpdateDto {
  /** Güncellenecek ürünler. Boşsa filtre kullanılır. */
  productIds: number[];
  categoryId?: number | null;
  brandId?: number | null;
  adjustmentType: string;
  /** Negatif değer indirim, pozitif zam anlamına gelir. */
  value: number;
  /** Üzeri çizili fiyat da güncellensin mi. */
  updateCompareAtPrice: boolean;
  /** Yuvarlama: "none" | "0.99" | "0.95" | "integer". Psikolojik fiyatlandırma için kullanılır. */
  rounding: string;
}

export interface DashboardSummaryDto {
  totalRevenue: number;
  revenueChangePercentage: number;
  orderCount: number;
  orderCountChangePercentage: number;
  averageOrderValue: number;
  newCustomerCount: number;
  pendingOrderCount: number;
  pendingFulfillmentCount: number;
  pendingReturnCount: number;
  pendingReviewCount: number;
  lowStockCount: number;
  outOfStockCount: number;
  abandonedCartCount: number;
  abandonedCartValue: number;
  currencyCode: string;
}

export interface SalesTrendPointDto {
  date: string;
  revenue: number;
  orderCount: number;
}

export interface TopProductDto {
  productId: number;
  title: string;
  sku?: string | null;
  imageUrl?: string | null;
  quantitySold: number;
  revenue: number;
}

export interface LowStockAlertDto {
  productVariantId: number;
  productTitle: string;
  variantTitle?: string | null;
  sku: string;
  available: number;
  threshold: number;
  imageUrl?: string | null;
}

export interface WebhookSubscriptionDto {
  id: number;
  name: string;
  topics: string[];
  targetUrl: string;
  isActive: boolean;
  lastTriggeredAt?: string | null;
  lastSuccessAt?: string | null;
  consecutiveFailureCount: number;
  /** İmza anahtarı yalnızca oluşturma anında bir kez döner. */
  secret?: string | null;
}

export interface WebhookSubscriptionUpsertDto {
  name: string;
  topics: string[];
  targetUrl: string;
  isActive: boolean;
  maxRetryCount: number;
}

export interface WebhookDeliveryDto {
  id: number;
  eventType: string;
  status: WebhookDeliveryStatus;
  attemptCount: number;
  responseStatusCode?: number | null;
  durationMs?: number | null;
  lastError?: string | null;
  createdAt: string;
  deliveredAt?: string | null;
}

export interface ApplicationLogDto {
  id: number;
  level: LogLevelType;
  message: string;
  logger?: string | null;
  exception?: string | null;
  requestPath?: string | null;
  statusCode?: number | null;
  durationMs?: number | null;
  userName?: string | null;
  ipAddress?: string | null;
  traceId?: string | null;
  loggedAt: string;
}

export interface LogQueryDto extends PagedRequest {
  level?: LogLevelType | null;
  from?: string | null;
  to?: string | null;
  traceId?: string | null;
  statusCode?: number | null;
}

export interface AuditLogDto {
  id: number;
  action: AuditAction;
  entityType: string;
  entityId?: string | null;
  entityLabel?: string | null;
  description?: string | null;
  changedFields: string[];
  userName?: string | null;
  userRoles?: string | null;
  ipAddress?: string | null;
  createdAt: string;
}

export interface AuditLogQueryDto extends PagedRequest {
  action?: AuditAction | null;
  entityType?: string | null;
  entityId?: string | null;
  userId?: string | null;
  from?: string | null;
  to?: string | null;
}

export interface LoginAttemptDto {
  id: number;
  attemptedEmail: string;
  result: LoginAttemptResult;
  ipAddress?: string | null;
  userAgent?: string | null;
  detail?: string | null;
  attemptedAt: string;
}

export interface LoginAttemptQueryDto extends PagedRequest {
  result?: LoginAttemptResult | null;
  email?: string | null;
  ipAddress?: string | null;
  from?: string | null;
  to?: string | null;
  /** Yalnızca başarısız denemeler. */
  failedOnly?: boolean | null;
}

export interface CalculationLineInput {
  productVariantId: number;
  productId: number;
  quantity: number;
  /** Yönetici tarafından elle girilen fiyat (taslak siparişlerde). */
  priceOverride?: number | null;
  /** Kalem bazlı elle indirim. */
  manualDiscount?: number | null;
}

export interface TaxAddressInput {
  countryCode: string;
  province?: string | null;
  postalCode?: string | null;
}

export interface PriceCalculationRequest {
  lines: CalculationLineInput[];
  userId?: string | null;
  customerEmail?: string | null;
  customerGroupId?: number | null;
  currencyCode: string;
  /** Uygulanacak kupon kodları. */
  discountCodes: string[];
  /** Kullanılacak hediye kartı kodları. */
  giftCardCodes: string[];
  shippingRateId?: number | null;
  shippingAddress?: TaxAddressInput | null;
  channel: OrderChannel;
  /** Müşteri KDV'den muaf mı (kurumsal / yurt dışı). */
  isTaxExempt: boolean;
}

export interface CalculatedLineDto {
  productVariantId: number;
  productId: number;
  productTitle: string;
  variantTitle?: string | null;
  sku: string;
  imageUrl?: string | null;
  productSlug?: string | null;
  brandName?: string | null;
  quantity: number;
  /** İndirim öncesi birim fiyat. */
  unitPrice: number;
  compareAtPrice?: number | null;
  unitCost?: number | null;
  /** UnitPrice * Quantity. */
  lineSubTotal: number;
  discountTotal: number;
  taxRate: number;
  taxTotal: number;
  /** Müşterinin bu kalem için ödeyeceği nihai tutar. */
  lineTotal: number;
  requiresShipping: boolean;
  isTaxable: boolean;
  weight?: number | null;
  weightUnit: WeightUnit;
  /** Stok yetersizse gerçekte alınabilecek adet. */
  availableQuantity?: number | null;
  isAvailable: boolean;
  unavailableReason?: string | null;
}

export interface AppliedDiscountDto {
  discountId?: number | null;
  discountCodeId?: number | null;
  code?: string | null;
  description: string;
  discountType: string;
  target: DiscountTarget;
  amount: number;
  isAutomatic: boolean;
}

export interface CalculatedTaxLineDto {
  title: string;
  rate: number;
  taxableAmount: number;
  amount: number;
  isIncludedInPrice: boolean;
}

export interface AppliedGiftCardDto {
  giftCardId: number;
  maskedCode: string;
  appliedAmount: number;
  remainingBalance: number;
}

export interface PriceCalculationResult {
  lines: CalculatedLineDto[];
  discounts: AppliedDiscountDto[];
  taxLines: CalculatedTaxLineDto[];
  giftCards: AppliedGiftCardDto[];
  subTotal: number;
  discountTotal: number;
  shippingTotal: number;
  shippingDiscountTotal: number;
  taxTotal: number;
  giftCardTotal: number;
  grandTotal: number;
  /** Hediye kartı düşüldükten sonra ödenmesi gereken tutar. */
  amountDue: number;
  currencyCode: string;
  taxMode: TaxInclusionMode;
  itemCount: number;
  totalWeightGrams: number;
  /** Kullanıcıya gösterilecek uyarılar: geçersiz kupon, stok azaldı, fiyat değişti gibi. Hesabı engellemez ama bilgilendirir. */
  warnings: string[];
  /** Ödeme adımına geçmeyi engelleyen hatalar. */
  blockingErrors: string[];
}

export interface DiscountDto {
  id: number;
  name: string;
  description?: string | null;
  type: DiscountType;
  target: DiscountTarget;
  status: DiscountStatus;
  isAutomatic: boolean;
  percentage?: number | null;
  amount?: number | null;
  maximumDiscountAmount?: number | null;
  minimumSubtotal?: number | null;
  minimumQuantity?: number | null;
  usageLimit?: number | null;
  usageLimitPerCustomer?: number | null;
  usageCount: number;
  startsAt: string;
  endsAt?: string | null;
  appliesTo: DiscountSelection;
  customerSelection: DiscountCustomerSelection;
  combination: DiscountCombination;
  priority: number;
  codes: string[];
  entitledIds: number[];
  excludedIds: number[];
  buyQuantity?: number | null;
  getQuantity?: number | null;
  getDiscountPercentage?: number | null;
}

export interface DiscountUpsertDto {
  name: string;
  description?: string | null;
  type: DiscountType;
  target: DiscountTarget;
  status: DiscountStatus;
  isAutomatic: boolean;
  /** Kodlu indirimlerde kupon kodları. Otomatik indirimde boş bırakılır. */
  codes: string[];
  percentage?: number | null;
  amount?: number | null;
  maximumDiscountAmount?: number | null;
  allocationMethod: DiscountAllocationMethod;
  appliesTo: DiscountSelection;
  entitledIds: number[];
  excludedIds: number[];
  customerSelection: DiscountCustomerSelection;
  customerIds: string[];
  customerGroupIds: number[];
  minimumSubtotal?: number | null;
  minimumQuantity?: number | null;
  buyQuantity?: number | null;
  getQuantity?: number | null;
  getDiscountPercentage?: number | null;
  getEntitledIds: number[];
  usageLimit?: number | null;
  usageLimitPerCustomer?: number | null;
  startsAt: string;
  endsAt?: string | null;
  combination: DiscountCombination;
  priority: number;
  maximumShippingAmount?: number | null;
  entitledShippingZoneIds: number[];
}

export interface DiscountQueryDto extends PagedRequest {
  status?: DiscountStatus | null;
  type?: DiscountType | null;
  isAutomatic?: boolean | null;
  /** Şu an geçerli olanlar. */
  activeNow?: boolean | null;
}

export interface ValidateDiscountCodeDto {
  code: string;
}

export interface GiftCardDto {
  id: number;
  maskedCode: string;
  initialValue: number;
  balance: number;
  currencyCode: string;
  status: GiftCardStatus;
  expiresAt?: string | null;
  recipientEmail?: string | null;
  recipientName?: string | null;
  createdAt: string;
  lastUsedAt?: string | null;
  /** Yalnızca oluşturma anında, bir kez döner. */
  plainCode?: string | null;
}

export interface GiftCardCreateDto {
  amount: number;
  currencyCode: string;
  expiresAt?: string | null;
  recipientEmail?: string | null;
  recipientName?: string | null;
  senderName?: string | null;
  personalMessage?: string | null;
  scheduledDeliveryAt?: string | null;
  note?: string | null;
  /** Oluşturulunca alıcıya e-posta gönderilsin mi. */
  sendEmail: boolean;
}

export interface GiftCardAdjustDto {
  amount: number;
  note?: string | null;
}

export interface CheckGiftCardDto {
  code: string;
}

export interface GiftCardQueryDto extends PagedRequest {
  status?: GiftCardStatus | null;
  recipientEmail?: string | null;
  hasBalance?: boolean | null;
}

export interface TaxClassDto {
  id: number;
  name: string;
  code: string;
  description?: string | null;
  isDefault: boolean;
  isActive: boolean;
  rates: TaxRateDto[];
}

export interface TaxRateDto {
  id: number;
  name: string;
  rate: number;
  countryCode?: string | null;
  province?: string | null;
  isActive: boolean;
  appliesToShipping: boolean;
}

export interface TaxClassUpsertDto {
  name: string;
  code: string;
  description?: string | null;
  isDefault: boolean;
  isActive: boolean;
  rates: TaxRateUpsertDto[];
}

export interface TaxRateUpsertDto {
  id?: number | null;
  name: string;
  rate: number;
  countryCode?: string | null;
  province?: string | null;
  isActive: boolean;
  appliesToShipping: boolean;
  priority: number;
}

export interface CartDto {
  publicId: string;
  currencyCode: string;
  items: CartItemDto[];
  itemCount: number;
  subTotal: number;
  discountTotal: number;
  shippingTotal: number;
  shippingDiscountTotal: number;
  taxTotal: number;
  giftCardTotal: number;
  grandTotal: number;
  amountDue: number;
  discounts: AppliedDiscountDto[];
  taxLines: CalculatedTaxLineDto[];
  giftCards: AppliedGiftCardDto[];
  shippingRateId?: number | null;
  note?: string | null;
  /** Bilgilendirme mesajları: fiyat değişti, stok azaldı vb. */
  warnings: string[];
  /** Ödeme adımına geçmeyi engelleyen sorunlar. */
  blockingErrors: string[];
  canCheckout: boolean;
  /** Ücretsiz kargoya kalan tutar; 0 ise ücretsiz kargo kazanılmıştır. */
  remainingForFreeShipping?: number | null;
}

export interface CartItemDto {
  id: number;
  productId: number;
  productVariantId: number;
  productTitle: string;
  variantTitle?: string | null;
  sku: string;
  productSlug?: string | null;
  brandName?: string | null;
  imageUrl?: string | null;
  quantity: number;
  unitPrice: number;
  compareAtPrice?: number | null;
  lineSubTotal: number;
  discountTotal: number;
  lineTotal: number;
  isAvailable: boolean;
  unavailableReason?: string | null;
  availableQuantity?: number | null;
  /** Sepete eklendiğinden beri fiyat değiştiyse eski fiyat. */
  priceChangedFrom?: number | null;
  isGiftWrapped: boolean;
  giftMessage?: string | null;
}

export interface AddToCartDto {
  productVariantId: number;
  quantity: number;
  isGiftWrapped: boolean;
  giftMessage?: string | null;
  /** Kişiselleştirme bilgileri (isim baskısı vb.). */
  customAttributes?: Record<string, string> | null;
}

export interface UpdateCartItemDto {
  quantity: number;
  isGiftWrapped?: boolean | null;
  giftMessage?: string | null;
}

export interface ApplyDiscountCodeDto {
  code: string;
}

export interface ApplyGiftCardDto {
  code: string;
}

export interface SetCartShippingDto {
  shippingRateId?: number | null;
  shippingAddressId?: number | null;
  /** Üye olmayan müşteriler için adres bilgisi. */
  shippingAddress?: AddressInputDto | null;
}

export interface SetCartNoteDto {
  note?: string | null;
}

export interface AddressInputDto {
  firstName: string;
  lastName: string;
  company?: string | null;
  phone: string;
  addressLine1: string;
  addressLine2?: string | null;
  district: string;
  city: string;
  postalCode?: string | null;
  countryCode: string;
  deliveryNote?: string | null;
  isCorporate: boolean;
  taxOffice?: string | null;
  taxNumber?: string | null;
  nationalIdentityNumber?: string | null;
}

export interface CustomerAddressDto {
  id: number;
  title?: string | null;
  firstName: string;
  lastName: string;
  company?: string | null;
  phone: string;
  addressLine1: string;
  addressLine2?: string | null;
  district: string;
  city: string;
  postalCode?: string | null;
  countryCode: string;
  countryName: string;
  deliveryNote?: string | null;
  isDefaultShipping: boolean;
  isDefaultBilling: boolean;
  isCorporate: boolean;
  taxOffice?: string | null;
  taxNumber?: string | null;
}

export interface CustomerAddressUpsertDto extends AddressInputDto {
  title?: string | null;
  isDefaultShipping: boolean;
  isDefaultBilling: boolean;
}

export interface ShippingOptionDto {
  shippingRateId: number;
  name: string;
  description?: string | null;
  price: number;
  originalPrice?: number | null;
  isFree: boolean;
  currencyCode: string;
  speed: ShippingSpeed;
  minDeliveryDays?: number | null;
  maxDeliveryDays?: number | null;
  estimatedDeliveryFrom?: string | null;
  estimatedDeliveryTo?: string | null;
  carrierName?: string | null;
  carrierLogoUrl?: string | null;
  allowsCashOnDelivery: boolean;
  cashOnDeliveryFee: number;
}

export interface CheckoutDto {
  /** Misafir alışverişte zorunlu; üye ise hesaptan alınır. */
  email?: string | null;
  phone?: string | null;
  /** Kayıtlı adres kimliği veya yeni adres bilgisi verilmelidir. */
  shippingAddressId?: number | null;
  shippingAddress?: AddressInputDto | null;
  billingAddressId?: number | null;
  billingAddress?: AddressInputDto | null;
  /** Fatura adresi teslimat adresiyle aynı mı. */
  billingSameAsShipping: boolean;
  shippingRateId: number;
  /** Mağazadan teslim alma seçildiyse konum. */
  pickupLocationId?: number | null;
  paymentMethod: PaymentMethodType;
  installment: number;
  customerNote?: string | null;
  requiresInvoice: boolean;
  acceptsTerms: boolean;
  /** Misafir müşteri için hesap oluşturulsun mu. */
  createAccount: boolean;
  /** Yaş sınırlı ürün varsa onay. */
  confirmsAge: boolean;
  /** PayTR Direkt API için kart bilgileri */
  card?: PaymentCardInputDto | null;
}

export interface PaymentCardInputDto {
  ccOwner?: string;
  cardNumber?: string;
  expiryMonth?: string;
  expiryYear?: string;
  cvv?: string;
  installmentCount?: number;
  cardType?: string;
}

export interface PayTrBinDetailDto {
  success: boolean;
  binNumber?: string;
  cardFamily?: string;
  bankName?: string;
  cardType?: string;
  cardAssociation?: string;
  allowsInstallment: boolean;
  errorMessage?: string;
}

export interface CheckoutResultDto {
  orderPublicId: string;
  orderNumber: string;
  amountDue: number;
  currencyCode: string;
  financialStatus: FinancialStatus;
  /** 3D Secure gibi yönlendirme gerektiren ödemelerde tarayıcının gideceği adres. */
  paymentRedirectUrl?: string | null;
  /** Sağlayıcının döndürdüğü ve tarayıcıda gönderilecek HTML form. */
  paymentFormHtml?: string | null;
  /** PayTR iFrame gibi gömülü ödeme sayfası URL'si. */
  paymentIframeUrl?: string | null;
  /** PayTR iFrame token değeri. */
  paymentIframeToken?: string | null;
  requiresPaymentAction: boolean;
  /** Sipariş takip bağlantısı. */
  orderTrackingUrl?: string | null;
}

export interface OrderListItemDto {
  id: number;
  publicId: string;
  orderNumber: string;
  customerFullName?: string | null;
  email: string;
  status: OrderStatus;
  financialStatus: FinancialStatus;
  fulfillmentStatus: FulfillmentStatus;
  grandTotal: number;
  currencyCode: string;
  itemCount: number;
  channel: OrderChannel;
  tags?: string | null;
  riskScore?: number | null;
  createdAt: string;
  processedAt?: string | null;
  primaryImageUrl?: string | null;
}

export interface OrderDetailDto {
  id: number;
  publicId: string;
  orderNumber: string;
  email: string;
  phone?: string | null;
  customerFullName?: string | null;
  userId?: string | null;
  status: OrderStatus;
  financialStatus: FinancialStatus;
  fulfillmentStatus: FulfillmentStatus;
  currencyCode: string;
  subTotal: number;
  discountTotal: number;
  shippingTotal: number;
  shippingDiscountTotal: number;
  taxTotal: number;
  giftCardTotal: number;
  grandTotal: number;
  paidTotal: number;
  refundedTotal: number;
  outstandingTotal: number;
  taxMode: TaxInclusionMode;
  shippingMethodName?: string | null;
  isPickup: boolean;
  estimatedDeliveryFrom?: string | null;
  estimatedDeliveryTo?: string | null;
  customerNote?: string | null;
  /** Yalnızca yönetim görünümünde doldurulur. */
  staffNote?: string | null;
  tags?: string | null;
  invoiceNumber?: string | null;
  invoiceUrl?: string | null;
  channel: OrderChannel;
  riskScore?: number | null;
  createdAt: string;
  processedAt?: string | null;
  paidAt?: string | null;
  fulfilledAt?: string | null;
  deliveredAt?: string | null;
  cancelledAt?: string | null;
  cancelReason?: OrderCancelReason | null;
  lines: OrderLineDto[];
  shippingAddress?: OrderAddressDto | null;
  billingAddress?: OrderAddressDto | null;
  discounts: OrderDiscountDto[];
  taxLines: OrderTaxLineDto[];
  fulfillments: FulfillmentDto[];
  transactions: PaymentTransactionDto[];
  refunds: RefundDto[];
  timeline: OrderEventDto[];
  /** Müşteri bu siparişi iptal edebilir mi. */
  canCancel: boolean;
  /** İade talebi açılabilir mi. */
  canReturn: boolean;
}

export interface OrderLineDto {
  id: number;
  productId?: number | null;
  productVariantId?: number | null;
  productTitle: string;
  variantTitle?: string | null;
  sku: string;
  productSlug?: string | null;
  brandName?: string | null;
  imageUrl?: string | null;
  quantity: number;
  unitPrice: number;
  lineSubTotal: number;
  discountTotal: number;
  taxRate: number;
  taxTotal: number;
  lineTotal: number;
  fulfilledQuantity: number;
  refundedQuantity: number;
  cancelledQuantity: number;
  fulfillableQuantity: number;
  requiresShipping: boolean;
  isGiftWrapped: boolean;
  giftMessage?: string | null;
  /** Dijital ürünse indirme bağlantısı (yalnızca sahibine). */
  downloadUrl?: string | null;
  /** Maliyet ve kâr yalnızca yönetim görünümünde. */
  unitCost?: number | null;
}

export interface OrderAddressDto {
  firstName: string;
  lastName: string;
  company?: string | null;
  phone: string;
  addressLine1: string;
  addressLine2?: string | null;
  district: string;
  city: string;
  postalCode?: string | null;
  countryName: string;
  deliveryNote?: string | null;
  isCorporate: boolean;
  taxOffice?: string | null;
  taxNumber?: string | null;
}

export interface OrderDiscountDto {
  code?: string | null;
  description: string;
  discountType: string;
  amount: number;
}

export interface OrderTaxLineDto {
  title: string;
  rate: number;
  taxableAmount: number;
  amount: number;
  isIncludedInPrice: boolean;
}

export interface OrderEventDto {
  id: number;
  eventType: string;
  message: string;
  performedByName?: string | null;
  isSystemGenerated: boolean;
  createdAt: string;
}

export interface FulfillmentDto {
  id: number;
  fulfillmentNumber: string;
  state: FulfillmentState;
  carrierName?: string | null;
  trackingNumber?: string | null;
  trackingUrl?: string | null;
  trackingStatus: ShipmentTrackingStatus;
  locationName?: string | null;
  shippedAt?: string | null;
  estimatedDeliveryAt?: string | null;
  deliveredAt?: string | null;
  deliveredTo?: string | null;
  lines: FulfillmentLineDto[];
  trackingEvents: FulfillmentTrackingEventDto[];
}

export interface FulfillmentLineDto {
  orderLineId: number;
  productTitle: string;
  variantTitle?: string | null;
  sku: string;
  quantity: number;
}

export interface FulfillmentTrackingEventDto {
  status: ShipmentTrackingStatus;
  description?: string | null;
  location?: string | null;
  occurredAt: string;
}

export interface CreateFulfillmentDto {
  /** Sevk edilecek kalemler: OrderLineId -> adet. */
  lines: FulfillmentLineInputDto[];
  locationId: number;
  carrierId?: number | null;
  trackingNumber?: string | null;
  trackingUrl?: string | null;
  packageWeight?: number | null;
  note?: string | null;
  /** Müşteriye kargo bildirimi e-postası gönderilsin mi. */
  notifyCustomer: boolean;
}

export interface FulfillmentLineInputDto {
  orderLineId: number;
  quantity: number;
}

export interface UpdateTrackingDto {
  trackingNumber?: string | null;
  carrierId?: number | null;
  status?: ShipmentTrackingStatus | null;
  notifyCustomer: boolean;
}

export interface PaymentTransactionDto {
  id: number;
  kind: PaymentTransactionKind;
  status: PaymentTransactionStatus;
  methodType: PaymentMethodType;
  amount: number;
  currencyCode: string;
  gateway: string;
  gatewayTransactionId?: string | null;
  cardLastFour?: string | null;
  cardBrand?: string | null;
  installment: number;
  is3DSecure: boolean;
  gatewayMessage?: string | null;
  processedAt?: string | null;
  createdAt: string;
}

export interface ManualPaymentDto {
  amount: number;
  methodType: PaymentMethodType;
  referenceNumber?: string | null;
  note?: string | null;
}

export interface RefundDto {
  id: number;
  refundNumber: string;
  status: RefundStatus;
  subTotal: number;
  taxTotal: number;
  shippingTotal: number;
  totalAmount: number;
  currencyCode: string;
  issuedAsStoreCredit: boolean;
  reason?: string | null;
  processedAt?: string | null;
  createdAt: string;
  lines: RefundLineDto[];
}

export interface RefundLineDto {
  orderLineId: number;
  productTitle: string;
  sku: string;
  quantity: number;
  amount: number;
  taxAmount: number;
  restock: boolean;
}

export interface CreateRefundDto {
  lines: RefundLineInputDto[];
  /** Kalemlerden bağımsız ek iade tutarı (örn. jest indirimi). */
  additionalAmount: number;
  /** Kargo bedeli de iade edilsin mi. */
  refundShipping: boolean;
  /** Karta iade yerine mağaza kredisi (hediye kartı) olarak ver. */
  issueAsStoreCredit: boolean;
  reason?: string | null;
  note?: string | null;
  notifyCustomer: boolean;
}

export interface RefundLineInputDto {
  orderLineId: number;
  quantity: number;
  /** Ürün stoğa geri alınsın mı. */
  restock: boolean;
  restockLocationId?: number | null;
  disposition: ReturnDisposition;
}

export interface ReturnRequestDto {
  id: number;
  returnNumber: string;
  orderId: number;
  orderNumber: string;
  status: ReturnStatus;
  reason: ReturnReason;
  customerNote?: string | null;
  staffNote?: string | null;
  requestedAmount: number;
  approvedAmount?: number | null;
  isExchange: boolean;
  returnTrackingNumber?: string | null;
  returnCarrierName?: string | null;
  returnLabelUrl?: string | null;
  requestedAt: string;
  approvedAt?: string | null;
  receivedAt?: string | null;
  completedAt?: string | null;
  rejectionReason?: string | null;
  lines: ReturnLineDto[];
  customerImageUrls: string[];
}

export interface ReturnLineDto {
  id: number;
  orderLineId: number;
  productTitle: string;
  variantTitle?: string | null;
  sku: string;
  imageUrl?: string | null;
  quantityRequested: number;
  quantityReceived: number;
  quantityApproved: number;
  reason: ReturnReason;
  reasonDetail?: string | null;
  disposition: ReturnDisposition;
  refundAmount: number;
}

export interface CreateReturnRequestDto {
  orderId: number;
  lines: ReturnLineInputDto[];
  reason: ReturnReason;
  customerNote?: string | null;
  imageUrls: string[];
  /** Para iadesi yerine değişim talebi. */
  isExchange: boolean;
}

export interface ReturnLineInputDto {
  orderLineId: number;
  quantity: number;
  reason?: ReturnReason | null;
  reasonDetail?: string | null;
  /** Değişim talebinde istenen yeni varyant. */
  exchangeVariantId?: number | null;
}

export interface ProcessReturnDto {
  status: ReturnStatus;
  staffNote?: string | null;
  rejectionReason?: string | null;
  approvedAmount?: number | null;
  returnTrackingNumber?: string | null;
  returnCarrierName?: string | null;
  returnToLocationId?: number | null;
  /** İnceleme sonucu kalem bazlı kabul edilen adetler. */
  inspectionLines: ReturnInspectionLineDto[];
  notifyCustomer: boolean;
}

export interface ReturnInspectionLineDto {
  returnLineId: number;
  quantityReceived: number;
  quantityApproved: number;
  disposition: ReturnDisposition;
}

export interface OrderQueryDto extends PagedRequest {
  status?: OrderStatus | null;
  financialStatus?: FinancialStatus | null;
  fulfillmentStatus?: FulfillmentStatus | null;
  channel?: OrderChannel | null;
  userId?: string | null;
  email?: string | null;
  from?: string | null;
  to?: string | null;
  minTotal?: number | null;
  maxTotal?: number | null;
  tag?: string | null;
  /** Risk skoru bu değerin üzerinde olanlar. */
  minRiskScore?: number | null;
  hasOpenReturn?: boolean | null;
}

export interface CancelOrderDto {
  reason: OrderCancelReason;
  note?: string | null;
  /** Stok geri alınsın mı. */
  restockItems: boolean;
  /** Ödeme alındıysa iade edilsin mi. */
  refundPayment: boolean;
  notifyCustomer: boolean;
}

export interface UpdateOrderNoteDto {
  staffNote?: string | null;
  tags?: string | null;
}

export interface CreateManualOrderDto {
  email: string;
  userId?: string | null;
  phone?: string | null;
  lines: ManualOrderLineDto[];
  shippingAddress: AddressInputDto;
  billingAddress?: AddressInputDto | null;
  shippingRateId?: number | null;
  shippingOverride?: number | null;
  discountCodes: string[];
  customerNote?: string | null;
  staffNote?: string | null;
  /** Sipariş taslak olarak mı kaydedilsin (ödeme beklemeden). */
  saveAsDraft: boolean;
  /** Müşteriye ödeme bağlantısı e-postası gönderilsin mi. */
  sendPaymentLink: boolean;
}

export interface ManualOrderLineDto {
  productVariantId: number;
  quantity: number;
  /** Yöneticinin belirlediği özel fiyat. */
  priceOverride?: number | null;
  manualDiscount?: number | null;
}

export interface OrderTrackingDto {
  orderNumber: string;
  status: OrderStatus;
  financialStatus: FinancialStatus;
  fulfillmentStatus: FulfillmentStatus;
  createdAt: string;
  estimatedDeliveryFrom?: string | null;
  estimatedDeliveryTo?: string | null;
  lines: OrderLineDto[];
  fulfillments: FulfillmentDto[];
  timeline: OrderEventDto[];
  grandTotal: number;
  currencyCode: string;
  /** Adres kısmen maskelenir; bağlantıyı ele geçiren kişi tam adresi göremez. */
  maskedShippingAddress?: string | null;
}

export interface ShippingZoneDto {
  id: number;
  name: string;
  scope: ShippingZoneScope;
  isActive: boolean;
  isFallback: boolean;
  priority: number;
  regions: ShippingZoneRegionDto[];
  rates: ShippingRateDto[];
}

export interface ShippingZoneRegionDto {
  id: number;
  countryCode: string;
  province?: string | null;
  district?: string | null;
  postalCodeFrom?: string | null;
  postalCodeTo?: string | null;
}

export interface ShippingRateDto {
  id: number;
  name: string;
  description?: string | null;
  rateType: ShippingRateType;
  speed: ShippingSpeed;
  price: number;
  currencyCode: string;
  carrierId?: number | null;
  carrierName?: string | null;
  minOrderSubtotal?: number | null;
  maxOrderSubtotal?: number | null;
  minWeightGrams?: number | null;
  maxWeightGrams?: number | null;
  minItemCount?: number | null;
  maxItemCount?: number | null;
  freeAboveSubtotal?: number | null;
  perKilogramSurcharge?: number | null;
  perItemSurcharge?: number | null;
  minDeliveryDays?: number | null;
  maxDeliveryDays?: number | null;
  sameDayCutoffHour?: number | null;
  isActive: boolean;
  displayOrder: number;
  allowsCashOnDelivery: boolean;
  cashOnDeliveryFee: number;
}

export interface ShippingZoneUpsertDto {
  name: string;
  scope: ShippingZoneScope;
  isActive: boolean;
  /** Hiçbir bölgeye uymayan adresler için yedek bölge. */
  isFallback: boolean;
  priority: number;
  regions: ShippingZoneRegionUpsertDto[];
  rates: ShippingRateUpsertDto[];
}

export interface ShippingZoneRegionUpsertDto {
  id?: number | null;
  countryCode: string;
  province?: string | null;
  district?: string | null;
  postalCodeFrom?: string | null;
  postalCodeTo?: string | null;
}

export interface ShippingRateUpsertDto {
  id?: number | null;
  name: string;
  description?: string | null;
  rateType: ShippingRateType;
  speed: ShippingSpeed;
  price: number;
  carrierId?: number | null;
  minOrderSubtotal?: number | null;
  maxOrderSubtotal?: number | null;
  minWeightGrams?: number | null;
  maxWeightGrams?: number | null;
  minItemCount?: number | null;
  maxItemCount?: number | null;
  freeAboveSubtotal?: number | null;
  perKilogramSurcharge?: number | null;
  perItemSurcharge?: number | null;
  minDeliveryDays?: number | null;
  maxDeliveryDays?: number | null;
  sameDayCutoffHour?: number | null;
  isActive: boolean;
  displayOrder: number;
  allowsCashOnDelivery: boolean;
  cashOnDeliveryFee: number;
  isTaxable: boolean;
  taxClassId?: number | null;
}

export interface CarrierDto {
  id: number;
  name: string;
  code: string;
  logoUrl?: string | null;
  isActive: boolean;
  trackingUrlTemplate?: string | null;
  hasApiIntegration: boolean;
  /** API anahtarı maskelenmiş olarak döner; tam değer hiçbir zaman gönderilmez. */
  maskedApiKey?: string | null;
  accountNumber?: string | null;
  supportsCashOnDelivery: boolean;
  supportsLabelGeneration: boolean;
  displayOrder: number;
}

export interface CarrierUpsertDto {
  name: string;
  code: string;
  logoUrl?: string | null;
  isActive: boolean;
  trackingUrlTemplate?: string | null;
  hasApiIntegration: boolean;
  apiBaseUrl?: string | null;
  /** Yeni API anahtarı. Boş bırakılırsa mevcut anahtar korunur; böylece panelde maskelenmiş değeri kaydetmek anahtarı bozmaz. */
  apiKey?: string | null;
  apiSecret?: string | null;
  accountNumber?: string | null;
  supportsCashOnDelivery: boolean;
  supportsLabelGeneration: boolean;
  displayOrder: number;
  maxWeightGrams?: number | null;
}

export interface CartContext {
  userId?: string | null;
  sessionKey?: string | null;
}

export interface PaymentInitiationRequest {
  orderId: number;
  orderNumber: string;
  amount: number;
  currencyCode: string;
  installment: number;
  customerEmail: string;
  customerName?: string | null;
  customerPhone?: string | null;
  ipAddress?: string | null;
  /** Ödeme tamamlanınca tarayıcının döneceği adres. */
  callbackUrl?: string | null;
  idempotencyKey?: string | null;
}

export interface PaymentInitiationResult {
  success: boolean;
  gatewayTransactionId?: string | null;
  /** 3D Secure yönlendirmesi gerekiyorsa hedef adres. */
  redirectUrl?: string | null;
  formHtml?: string | null;
  requiresAction: boolean;
  /** Yönlendirme gerekmiyorsa ödeme anında tamamlandı mı. */
  isCompleted: boolean;
  errorMessage?: string | null;
  errorCode?: string | null;
  cardLastFour?: string | null;
  cardBrand?: string | null;
}

export interface PaymentCaptureResult {
  success: boolean;
  transactionId?: string | null;
  errorMessage?: string | null;
}

export interface PaymentRefundResult {
  success: boolean;
  transactionId?: string | null;
  errorMessage?: string | null;
}

export interface PaymentVoidResult {
  success: boolean;
  errorMessage?: string | null;
}

export interface EmailMessage {
  to: string;
  cc: string[];
  bcc: string[];
  subject: string;
  htmlBody: string;
  textBody?: string | null;
  attachments: EmailAttachment[];
  replyTo?: string | null;
}

export interface EmailAttachment {
  fileName: string;
  content: number[];
  contentType: string;
}

export interface FileUploadResult {
  success: boolean;
  url?: string | null;
  storagePath?: string | null;
  fileName?: string | null;
  contentType?: string | null;
  sizeBytes: number;
  width?: number | null;
  height?: number | null;
  fileHash?: string | null;
  errorMessage?: string | null;
}

export interface AdminProductsController {
  productIds: number[];
  status: ProductStatus;
}

export interface AdminOrdersController {
  note?: string | null;
}

export interface MyOrdersController {
  reason?: string | null;
}

export interface OrderTrackingController {
  orderNumber: string;
  email: string;
}

export interface WishlistController {
  productId: number;
  variantId?: number | null;
}

export interface ShippingWebhookController {
  trackingNumber: string;
  status: ShipmentTrackingStatus;
  description?: string | null;
  location?: string | null;
  occurredAt?: string | null;
}

export interface SearchAutocompleteDto {
  products: AutocompleteProductDto[];
  categories: AutocompleteCategoryDto[];
  brands: AutocompleteBrandDto[];
  suggestions: string[];
}

export interface AutocompleteProductDto {
  id: number;
  title: string;
  slug: string;
  price: number;
  compareAtPrice?: number | null;
  primaryImageUrl?: string | null;
  brandName?: string | null;
  sku?: string | null;
}

export interface AutocompleteCategoryDto {
  id: number;
  name: string;
  slug: string;
  productCount: number;
}

export interface AutocompleteBrandDto {
  id: number;
  name: string;
  slug: string;
  logoUrl?: string | null;
}

export interface CustomerCouponDto {
  id: number;
  discountId: number;
  code: string;
  title: string;
  description?: string | null;
  type: 'Percentage' | 'FixedAmount' | 'FreeShipping' | 'BuyXGetY';
  value: number;
  minimumSubtotal?: number | null;
  maximumDiscountAmount?: number | null;
  appliesTo: 'All' | 'EntitledCategories' | 'EntitledProducts';
  entitledCategoryNames: string[];
  startsAt?: string | null;
  endsAt?: string | null;
  status: 'Active' | 'Used' | 'Expired';
  isUsed: boolean;
  isExpired: boolean;
  remainingUsageCount: number;
}


